/**
 * Typing indicator utilities using Supabase Realtime
 */

import { supabase } from './supabaseClient';

const typingChannels = new Map();

/**
 * Subscribe to typing indicators for a conversation
 * @param {string} conversationId - Conversation ID
 * @param {string} currentUserId - Current user ID
 * @param {function} callback - Callback function that receives typing status updates
 * @returns {function} Unsubscribe function
 */
export const subscribeToTyping = (conversationId, currentUserId, callback) => {
  const channelName = `typing:${conversationId}`;
  
  // Remove existing channel if any
  if (typingChannels.has(channelName)) {
    supabase.removeChannel(typingChannels.get(channelName));
  }

  const channel = supabase
    .channel(channelName)
    .on('presence', { event: 'sync' }, () => {
      const state = channel.presenceState();
      // Check all users in the channel, filter out current user and only show if they're actually typing
      let someoneTyping = false;
      Object.values(state).forEach((presences) => {
        presences.forEach((presence) => {
          if (presence.userId !== currentUserId && presence.typing === true) {
            someoneTyping = true;
          }
        });
      });
      callback(someoneTyping);
    })
    .on('presence', { event: 'join' }, ({ key, newPresences }) => {
      // Check if any of the new presences are typing (and not the current user)
      const someoneTyping = newPresences.some(
        p => p.userId !== currentUserId && p.typing === true
      );
      callback(someoneTyping);
    })
    .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
      // Check remaining users
      const state = channel.presenceState();
      let someoneTyping = false;
      Object.values(state).forEach((presences) => {
        presences.forEach((presence) => {
          if (presence.userId !== currentUserId && presence.typing === true) {
            someoneTyping = true;
          }
        });
      });
      callback(someoneTyping);
    })
    .subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        await channel.track({
          userId: currentUserId,
          typing: false,
          onlineAt: new Date().toISOString(),
        });
      }
    });

  typingChannels.set(channelName, channel);

  return () => {
    channel.untrack();
    supabase.removeChannel(channel);
    typingChannels.delete(channelName);
  };
};

/**
 * Broadcast typing status
 * @param {string} conversationId - Conversation ID
 * @param {string} userId - User ID
 * @param {boolean} isTyping - Whether user is typing
 */
export const broadcastTyping = async (conversationId, userId, isTyping) => {
  const channelName = `typing:${conversationId}`;
  const channel = typingChannels.get(channelName);
  
  if (channel) {
    await channel.track({
      userId,
      typing: isTyping,
      onlineAt: new Date().toISOString(),
    });
  }
};

